// 2D outlines of the 3D table (TableModel.ts builds the meshes from these). Pure maths, no three.js, so the jest
// test (tableShapes.test.ts) can check that what is drawn lines up with the physics:
//   - the cushion noses and pocket liners follow the cushion line exactly (the physics walls, CUSHION_LINE),
//   - each dark pocket opening is the part of its pocket polygon inside the cushion line: a ball whose centre
//     crosses the edge of the dark area is potted (Game.UpdateState),
//   - the D, baulk line and spots come from shared/rules.ts.
//
// Coordinates are the physics plane: x across the table, y along it (three.js z). Heights are three.js y.
import {BALL_RADIUS, CUSHION_LINE, TABLE, Vec} from "./shared/rules";
import {POCKETS} from "./tableGeometry";

/** Height of the ball centres (Balls.ts BALL_HEIGHT). */
export const BALL_CENTRE_Y = 7.926842;
/** The cloth: the balls rest on it. */
export const BED_Y = BALL_CENTRE_Y - BALL_RADIUS;
/** Cushion nose (where a ball touches it, a little above the ball's equator), cushion top, rail top. */
export const NOSE_Y = BALL_CENTRE_Y + 0.035;
export const CUSHION_TOP_Y = 8.03;
export const RAIL_TOP_Y = 8.05;
/** Bottom of the rail's outer edge, bottom of the frame (apron) under it. */
export const RAIL_BOTTOM_Y = 7.79;
export const FRAME_BOTTOM_Y = 7.08;
/** Depth of the dark pocket openings below the cloth. */
export const POCKET_DEPTH = 0.42;
/** Wood from the cushion line to the outside of the table (as the old model, ~1.14 each side). */
export const RAIL_WIDTH = 1.14;
/** Width of the cushion's flat top band before the rail starts (from the cushion line, straight cushions). */
export const CUSHION_WIDTH = 0.3;
/** Width of the rounded nose part of the cushion profile. */
export const NOSE_WIDTH = 0.1;

export const TABLE_CENTRE: Vec = [(TABLE.xMin + TABLE.xMax) / 2, (TABLE.yMin + TABLE.yMax) / 2];

const dist = (a: Vec, b: Vec) => Math.hypot(a[0] - b[0], a[1] - b[1]);

/** The cushion line as a closed path for drawing: no repeated points (CUSHION_LINE repeats one and closes itself). */
export function cushionPath(): Vec[] {
    const out: Vec[] = [];
    for (const p of CUSHION_LINE) {
        if (out.length && dist(out[out.length - 1], p) < 1e-6) continue;
        out.push([p[0], p[1]]);
    }
    if (dist(out[0], out[out.length - 1]) < 1e-6) out.pop();
    return out;
}

/** Edges of the cushion line up to this long are on its curved parts (the jaws and the backs of the pockets). */
export const CURVE_MAX_EDGE = 0.6;
/** A vertex where the line turns more than this (radians) is a corner: the drawn curve keeps it sharp. */
export const CURVE_MAX_TURN = 0.7;

const unit = (a: Vec, b: Vec): Vec => { const l = dist(a, b) || 1; return [(b[0] - a[0]) / l, (b[1] - a[1]) / l]; };

function turn(prev: Vec, p: Vec, next: Vec) {
    const u = unit(prev, p), v = unit(p, next);
    return Math.acos(Math.max(-1, Math.min(1, u[0] * v[0] + u[1] * v[1])));
}

/**
 * The cushion line for drawing, with the curved parts (jaws, pocket backs) smoothed: every physics point is kept,
 * and each short edge between them gets `subdivisions - 1` extra points on a centripetal Catmull-Rom curve through
 * its neighbours. Next to a long straight cushion the curve leaves along the cushion's direction, and at a sharp
 * corner (a turn over CURVE_MAX_TURN) it leaves along its own edge, so tips stay tips. The extra points stay within
 * a few thousandths of the physics walls (tableShapes.test.ts measures it). subdivisions = 1: cushionPath() itself.
 */
export function smoothCushionPath(subdivisions: number): Vec[] {
    const P = cushionPath();
    const n = P.length;
    if (subdivisions <= 1) return P;
    const at = (i: number) => P[((i % n) + n) % n];
    const out: Vec[] = [];
    for (let i = 0; i < n; i++) {
        const a = at(i), b = at(i + 1);
        out.push(a);
        const len = dist(a, b);
        if (len > CURVE_MAX_EDGE || len < 1e-9) continue;
        const prev = at(i - 1), next = at(i + 2);
        const phantom = (from: Vec, to: Vec, neighbour: Vec, sign: number): Vec => {
            // the control point before `from` (sign -1) or after `to` (sign +1)
            const edge = sign < 0 ? dist(neighbour, from) : dist(to, neighbour);
            const t = sign < 0 ? turn(neighbour, from, to) : turn(from, to, neighbour);
            if (t > CURVE_MAX_TURN) return sign < 0 ? [2 * from[0] - to[0], 2 * from[1] - to[1]] : [2 * to[0] - from[0], 2 * to[1] - from[1]];
            if (edge > CURVE_MAX_EDGE) {
                const d = sign < 0 ? unit(neighbour, from) : unit(to, neighbour);
                const p = sign < 0 ? from : to;
                return [p[0] + sign * d[0] * len, p[1] + sign * d[1] * len];
            }
            return neighbour;
        };
        const p0 = phantom(a, b, prev, -1), p3 = phantom(a, b, next, 1);
        // centripetal Catmull-Rom (alpha 0.5) from a to b
        const tj = (x: Vec, y: Vec) => Math.max(1e-9, Math.sqrt(dist(x, y)));
        const t0 = 0, t1 = t0 + tj(p0, a), t2 = t1 + tj(a, b), t3 = t2 + tj(b, p3);
        const lerp = (x: Vec, y: Vec, ta: number, tb: number, t: number): Vec =>
            [x[0] * (tb - t) / (tb - ta) + y[0] * (t - ta) / (tb - ta), x[1] * (tb - t) / (tb - ta) + y[1] * (t - ta) / (tb - ta)];
        for (let k = 1; k < subdivisions; k++) {
            const t = t1 + (t2 - t1) * k / subdivisions;
            const A1 = lerp(p0, a, t0, t1, t), A2 = lerp(a, b, t1, t2, t), A3 = lerp(b, p3, t2, t3, t);
            const B1 = lerp(A1, A2, t0, t2, t), B2 = lerp(A2, A3, t1, t3, t);
            out.push(lerp(B1, B2, t1, t2, t));
        }
    }
    return out;
}

/** Distance from p to the nearest edge of a closed polyline. */
export function distanceToPath(p: Vec, path: Vec[]): number {
    let best = Infinity;
    for (let i = 0; i < path.length; i++) {
        const a = path[i], b = path[(i + 1) % path.length];
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const l2 = dx * dx + dy * dy;
        const t = l2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2)) : 0;
        best = Math.min(best, Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy));
    }
    return best;
}

export function signedArea(poly: Vec[]) {
    let a = 0;
    for (let i = 0; i < poly.length; i++) {
        const p = poly[i], q = poly[(i + 1) % poly.length];
        a += p[0] * q[1] - q[0] * p[1];
    }
    return a / 2;
}

export function insidePolygon(p: Vec, poly: Vec[]) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const a = poly[i], b = poly[j];
        if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < (b[0] - a[0]) * (p[1] - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside;
    }
    return inside;
}

/** Splits long edges so no edge is longer than maxLen (keeps every original point). */
export function densify(path: Vec[], maxLen: number, closed = true): Vec[] {
    const out: Vec[] = [];
    const n = closed ? path.length : path.length - 1;
    for (let i = 0; i < n; i++) {
        const a = path[i], b = path[(i + 1) % path.length];
        const k = Math.max(1, Math.ceil(dist(a, b) / maxLen));
        for (let j = 0; j < k; j++) out.push([a[0] + (b[0] - a[0]) * j / k, a[1] + (b[1] - a[1]) * j / k]);
    }
    if (!closed) out.push(path[path.length - 1]);
    return out;
}

/**
 * Per-vertex offset directions of a closed path, pointing away from the inside of the polygon, scaled so that
 * p + n * d is at distance d from both adjacent edges (mitre, limited to 2x at sharp corners).
 */
export function outwardMitres(path: Vec[]): Vec[] {
    const ccw = signedArea(path) > 0;
    const n = path.length;
    const edgeNormal = (i: number): Vec => {
        const a = path[i], b = path[(i + 1) % n];
        const dx = b[0] - a[0], dy = b[1] - a[1];
        const l = Math.hypot(dx, dy) || 1;
        // right-hand normal of a CCW polygon points outwards
        return ccw ? [dy / l, -dx / l] : [-dy / l, dx / l];
    };
    const out: Vec[] = [];
    for (let i = 0; i < n; i++) {
        const n1 = edgeNormal((i - 1 + n) % n), n2 = edgeNormal(i);
        let mx = n1[0] + n2[0], my = n1[1] + n2[1];
        const ml = Math.hypot(mx, my);
        if (ml < 1e-9) { out.push(n2); continue; }
        mx /= ml; my /= ml;
        const cos = mx * n2[0] + my * n2[1];
        const scale = Math.min(2, 1 / Math.max(cos, 1e-6));
        out.push([mx * scale, my * scale]);
    }
    return out;
}

export function offsetPath(path: Vec[], mitres: Vec[], d: number): Vec[] {
    return path.map((p, i) => [p[0] + mitres[i][0] * d, p[1] + mitres[i][1] * d] as Vec);
}

/** How far a point is outside the cushion rectangle (0 inside). */
export function outsideCushions(p: Vec) {
    const dx = Math.max(TABLE.xMin - p[0], 0, p[0] - TABLE.xMax);
    const dy = Math.max(TABLE.yMin - p[1], 0, p[1] - TABLE.yMax);
    return Math.hypot(dx, dy);
}

/**
 * 0 on the cushions and the start of the jaws (cloth), 1 round the back of a pocket (leather liner),
 * smooth in between.
 */
export function linerWeight(p: Vec) {
    const t = Math.max(0, Math.min(1, (outsideCushions(p) - 0.12) / 0.16));
    return t * t * (3 - 2 * t);
}

/** Distances along a ray (origin + t * dir, t > 0) where it crosses the edges of a closed polygon. */
export function rayCrossings(origin: Vec, dir: Vec, poly: Vec[]): number[] {
    const ts: number[] = [];
    for (let i = 0; i < poly.length; i++) {
        const a = poly[i], b = poly[(i + 1) % poly.length];
        const ex = b[0] - a[0], ey = b[1] - a[1];
        const den = dir[0] * ey - dir[1] * ex;
        if (Math.abs(den) < 1e-12) continue;
        const ax = a[0] - origin[0], ay = a[1] - origin[1];
        const t = (ax * ey - ay * ex) / den;
        const s = (ax * dir[1] - ay * dir[0]) / den;
        if (t > 1e-9 && s >= 0 && s <= 1) ts.push(t);
    }
    return ts;
}

export interface PocketHole {
    /** a point well inside the opening */
    centre: Vec;
    /** outline of the dark opening, counter-clockwise */
    outline: Vec[];
}

/** Where segments ab and cd cross (inclusive of their ends), or null. */
function segmentCrossing(a: Vec, b: Vec, c: Vec, d: Vec): Vec | null {
    const rx = b[0] - a[0], ry = b[1] - a[1], sx = d[0] - c[0], sy = d[1] - c[1];
    const den = rx * sy - ry * sx;
    if (Math.abs(den) < 1e-12) return null;
    const qx = c[0] - a[0], qy = c[1] - a[1];
    const t = (qx * sy - qy * sx) / den, u = (qx * ry - qy * rx) / den;
    if (t < -1e-12 || t > 1 + 1e-12 || u < -1e-12 || u > 1 + 1e-12) return null;
    return [a[0] + rx * t, a[1] + ry * t];
}

/**
 * The dark opening of pocket i: its pocket polygon (Table.Pockets) clipped to the inside of the cushion line.
 * `cushion` is the cushion line exactly as the liners are swept along it (TableModel: the drawn, densified path), so
 * the opening's edge along the back of the pocket uses the liners' own points and the two meet without a saw-tooth.
 * The outline is exact: the cushion points inside the pocket polygon, the pocket polygon's corners inside the cushion
 * line and the points where the two cross, in angle order round a point inside both (the region is star-shaped from
 * there; a point hidden behind a nearer edge would be left out).
 */
export function pocketHole(i: number, cushion: Vec[] = cushionPath()): PocketHole {
    const poly: Vec[] = POCKETS[i].vertices.map(v => [v.x, v.y] as Vec);
    let sx = 0, sy = 0, n = 0;
    const xs = poly.map(p => p[0]), ys = poly.map(p => p[1]);
    for (let x = Math.min(...xs); x <= Math.max(...xs); x += 0.02) {
        for (let y = Math.min(...ys); y <= Math.max(...ys); y += 0.02) {
            if (insidePolygon([x, y], poly) && insidePolygon([x, y], cushion)) { sx += x; sy += y; n++; }
        }
    }
    const centre: Vec = [sx / n, sy / n];
    const candidates: Vec[] = [];
    for (const p of cushion) if (insidePolygon(p, poly)) candidates.push(p);
    for (const p of poly) if (insidePolygon(p, cushion)) candidates.push(p);
    // only cushion edges near the pocket can cross it
    const r = Math.max(...poly.map(p => dist(p, centre))) + 0.01;
    for (let a = 0; a < cushion.length; a++) {
        const c0 = cushion[a], c1 = cushion[(a + 1) % cushion.length];
        if (dist(c0, centre) > r + dist(c0, c1)) continue;
        for (let k = 0; k < poly.length; k++) {
            const x = segmentCrossing(c0, c1, poly[k], poly[(k + 1) % poly.length]);
            if (x) candidates.push(x);
        }
    }
    const pts: {p: Vec, a: number}[] = [];
    for (const p of candidates) {
        const d = dist(p, centre);
        if (d < 1e-9 || pts.some(q => dist(q.p, p) < 1e-7)) continue;
        const dir: Vec = [(p[0] - centre[0]) / d, (p[1] - centre[1]) / d];
        const nearest = Math.min(...rayCrossings(centre, dir, poly), ...rayCrossings(centre, dir, cushion));
        if (d > nearest + 1e-6) continue;   // behind a nearer edge
        pts.push({p: [p[0], p[1]], a: Math.atan2(dir[1], dir[0])});
    }
    pts.sort((u, v) => u.a - v.a);
    return {centre, outline: pts.map(q => q.p)};
}

/**
 * Inner edge of the wooden rail: the cushion line pushed out by CUSHION_WIDTH (the cushion's flat top; round the
 * backs of the pockets, the leather, CUSHION_WIDTH - 0.15), traced radially from the middle of the table and taking the outermost crossing, so it is
 * a simple polygon even where the offset folds at the jaws.
 */
export function railInnerEdge(samples = 1440): Vec[] {
    const path = densify(cushionPath(), 0.05);
    const mitres = outwardMitres(path);
    // narrower behind the pockets (the left middle pocket reaches 0.8 behind its cushion line)
    const offset = path.map((p, i) => {
        const d = CUSHION_WIDTH - 0.15 * linerWeight(p);
        return [p[0] + mitres[i][0] * d, p[1] + mitres[i][1] * d] as Vec;
    });
    const c = TABLE_CENTRE;
    const out: Vec[] = [];
    for (let k = 0; k < samples; k++) {
        const a = 2 * Math.PI * k / samples;
        const dir: Vec = [Math.cos(a), Math.sin(a)];
        const t = Math.max(...rayCrossings(c, dir, offset));
        out.push([c[0] + dir[0] * t, c[1] + dir[1] * t]);
    }
    return simplify(out, 0.002);
}

/** Drops points of a closed polygon that lie within `tolerance` of the line through their neighbours. */
export function simplify(poly: Vec[], tolerance: number): Vec[] {
    const keep = poly.slice();
    for (let changed = true; changed;) {
        changed = false;
        for (let i = 0; i < keep.length && keep.length > 3; i++) {
            const a = keep[(i - 1 + keep.length) % keep.length], p = keep[i], b = keep[(i + 1) % keep.length];
            const dx = b[0] - a[0], dy = b[1] - a[1];
            const len = Math.hypot(dx, dy);
            const d = len > 0 ? Math.abs((p[0] - a[0]) * dy - (p[1] - a[1]) * dx) / len : dist(p, a);
            if (d < tolerance && len < 1.0) { keep.splice(i, 1); changed = true; i--; }
        }
    }
    return keep;
}

/** A rounded rectangle, counter-clockwise, `segments` points per corner. */
export function roundedRect(x0: number, y0: number, x1: number, y1: number, r: number, segments = 8): Vec[] {
    const out: Vec[] = [];
    const corners: [number, number, number][] = [[x1 - r, y0 + r, -Math.PI / 2], [x1 - r, y1 - r, 0], [x0 + r, y1 - r, Math.PI / 2], [x0 + r, y0 + r, Math.PI]];
    for (const [cx, cy, a0] of corners) {
        for (let j = 0; j <= segments; j++) {
            const a = a0 + (Math.PI / 2) * j / segments;
            out.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
        }
    }
    return out;
}

/** Outside of the table (outer edge of the rails), inset by `inset` (the rounded edge starts there). */
export function railOuterEdge(inset = 0, segments = 8): Vec[] {
    const r = 0.3 - inset;
    return roundedRect(TABLE.xMin - RAIL_WIDTH + inset, TABLE.yMin - RAIL_WIDTH + inset,
        TABLE.xMax + RAIL_WIDTH - inset, TABLE.yMax + RAIL_WIDTH - inset, r, segments);
}

/** Do any two non-adjacent edges of a closed polygon cross? */
export function selfIntersects(poly: Vec[]) {
    const n = poly.length;
    const cross = (o: Vec, a: Vec, b: Vec) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    for (let i = 0; i < n; i++) {
        const a = poly[i], b = poly[(i + 1) % n];
        for (let j = i + 2; j < n; j++) {
            if (i === 0 && j === n - 1) continue;
            const c = poly[j], d = poly[(j + 1) % n];
            const d1 = cross(c, d, a), d2 = cross(c, d, b), d3 = cross(a, b, c), d4 = cross(a, b, d);
            if (((d1 > 0) !== (d2 > 0)) && ((d3 > 0) !== (d4 > 0)) && d1 !== 0 && d2 !== 0 && d3 !== 0 && d4 !== 0) return true;
        }
    }
    return false;
}

/** Diamond markers on the rail tops: eighths of the long rails (not at the middle pockets), quarters of the short ones. */
export function diamondPositions(): {p: Vec, alongY: boolean}[] {
    const mid = RAIL_WIDTH * 0.62;
    const out: {p: Vec, alongY: boolean}[] = [];
    const L = TABLE.yMax - TABLE.yMin, W = TABLE.xMax - TABLE.xMin;
    for (let k = 1; k <= 7; k++) {
        if (k === 4) continue;
        const y = TABLE.yMin + L * k / 8;
        out.push({p: [TABLE.xMax + mid, y], alongY: true}, {p: [TABLE.xMin - mid, y], alongY: true});
    }
    for (let k = 1; k <= 3; k++) {
        const x = TABLE.xMin + W * k / 4;
        out.push({p: [x, TABLE.yMax + mid], alongY: false}, {p: [x, TABLE.yMin - mid], alongY: false});
    }
    return out;
}
